import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';

import { RequireAuth, RequirePlatformAdmin, RequireRole } from '@core/auth';
import { getEnv } from '@core/config/env';
import { AppShell, RootErrorBoundary } from '@core/layout';
import { Skeleton } from '@core/ui';

import {
  OrganizationDetailPage,
  OrganizationsPage,
  PlansPage,
  PlatformAdminsPage,
} from './features/platform/lazyPages';

// Every route is lazily loaded. For "/" this buys nothing directly -- that route IS the
// map -- but it keeps MapLibre (~900 kB raw) out of the bundle for anyone who lives in
// /trackers or /users, and lets the shell paint while the map chunk streams.
const FleetPage = lazy(() => import('./features/fleet/FleetPage'));
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const TrackersPage = lazy(() => import('./features/trackers/TrackersPage'));
const AssetsPage = lazy(() => import('./features/assets/AssetsPage'));
const TripsPage = lazy(() => import('./features/trips/TripsPage'));
const GeofencesPage = lazy(() => import('./features/geofences/GeofencesPage'));
// This file is the ROUTER named routes.tsx, and it now imports a FEATURE named routes.
// The collision is harmless and reads badly exactly once.
const RoutesPage = lazy(() => import('./features/routes/RoutesPage'));
const AlertsPage = lazy(() => import('./features/alerts/AlertsPage'));
const UsersPage = lazy(() => import('./features/users/UsersPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));

const SignInPage = lazy(() => import('./features/auth/SignInPage'));
const SignUpPage = lazy(() => import('./features/auth/SignUpPage'));
const ForbiddenPage = lazy(() => import('./features/auth/ForbiddenPage'));
const NotFoundPage = lazy(() => import('./features/auth/NotFoundPage'));

// Development-only design galleries. Public so they stay reachable before Clerk is set
// up, and so the design language can be reviewed without signing in. Deleted at task B14.
const DevGalleries = lazy(() => import('./dev/DevGalleries'));

/** A skeleton rather than a spinner: the page shape is known before it loads. */
function RouteFallback() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="rounded-card h-64 w-full" />
    </div>
  );
}

const page = (element: ReactNode) => <Suspense fallback={<RouteFallback />}>{element}</Suspense>;

const routes: RouteObject[] = [
  // Public. Clerk's own components own these paths, hence the splat: the sign-in flow
  // navigates to sub-paths like /sign-in/factor-one and /sign-in/sso-callback, and
  // without it those 404 mid-flow.
  { path: '/sign-in/*', element: page(<SignInPage />) },
  { path: '/sign-up/*', element: page(<SignUpPage />) },
  { path: '/dev', element: page(<DevGalleries />) },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RootErrorBoundary />,
        children: [
          { index: true, element: page(<FleetPage />) },
          { path: 'dashboard', element: page(<DashboardPage />) },
          { path: 'trackers', element: page(<TrackersPage />) },
          { path: 'assets', element: page(<AssetsPage />) },
          { path: 'trips', element: page(<TripsPage />) },
          // No RequireRole: a VIEWER should see planned routes, as they see geofences.
          // The mutations are gated on the service, which is the only gate that counts.
          { path: 'routes', element: page(<RoutesPage />) },
          { path: 'geofences', element: page(<GeofencesPage />) },
          { path: 'alerts', element: page(<AlertsPage />) },
          {
            path: 'users',
            // Route-level guard AND a hidden nav item. Both are UX; the API enforces it.
            element: <RequireRole role="ADMIN" fallback={page(<ForbiddenPage />)} />,
            children: [{ index: true, element: page(<UsersPage />) }],
          },
          { path: 'settings', element: page(<SettingsPage />) },
          {
            path: 'platform',
            // A property of the person, not of a membership -- so not RequireRole.
            element: <RequirePlatformAdmin fallback={page(<ForbiddenPage />)} />,
            children: [
              { index: true, element: <Navigate to="organizations" replace /> },
              { path: 'organizations', element: page(<OrganizationsPage />) },
              { path: 'organizations/:id', element: page(<OrganizationDetailPage />) },
              { path: 'plans', element: page(<PlansPage />) },
              { path: 'admins', element: page(<PlatformAdminsPage />) },
            ],
          },
          { path: '403', element: page(<ForbiddenPage />) },
          { path: '*', element: page(<NotFoundPage />) },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes, { basename: getEnv().BASENAME });
